import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useSessionManager } from '../hooks/useSessionManager';
import IdleWarningModal from '../components/IdleWarningModal';
import { getSessionConfig, SessionConfig } from '../config/session';

interface SessionContextType {
  sessionState: {
    isActive: boolean;
    isIdle: boolean;
    isWarning: boolean;
    timeRemaining: number;
    warningTimeRemaining: number;
  };
  continueSession: () => void;
  logoutNow: () => void;
  resetSession: () => void;
  handleActivity: () => void;
  isEnabled: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [config, setConfig] = useState<SessionConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const sessionConfig = await getSessionConfig();
        setConfig(sessionConfig);
      } catch (error) {
        console.error('Failed to load session config:', error);
        // Use default config if loading fails
        setConfig({
          sessionTimeoutMinutes: 60,
          idleTimeoutMinutes: 30,
          warningCountdownSeconds: 120,
          enabled: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  const sessionManager = useSessionManager(config || undefined);

  // If session management is disabled, just render children without session management
  if (isLoading) {
    return <>{children}</>;
  }

  if (!config?.enabled) {
    return <>{children}</>;
  }

  return (
    <SessionContext.Provider
      value={{
        ...sessionManager,
        isEnabled: config.enabled,
      }}
    >
      {children}
      <IdleWarningModal
        isOpen={sessionManager.sessionState.isWarning}
        timeRemaining={sessionManager.sessionState.warningTimeRemaining}
        onContinue={sessionManager.continueSession}
        onLogout={sessionManager.logoutNow}
      />
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextType {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
